import { create } from 'zustand';
import type { HistoryEvent, HistoryEventData, HistoryEventType } from '@machina/core';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';

// =============================================================================
// DB TYPE
// =============================================================================

interface DbHistoryEvent {
  id: string;
  campaign_id: string;
  scene_id: string | null;
  event_type: string;
  data: Record<string, unknown>;
  created_at: string;
}

function dbToHistoryEvent(db: DbHistoryEvent): HistoryEvent {
  return {
    id: db.id,
    campaignId: db.campaign_id,
    sceneId: db.scene_id ?? undefined,
    eventType: db.event_type as HistoryEventType,
    data: db.data as unknown as HistoryEventData,
    createdAt: db.created_at,
  };
}

// =============================================================================
// STORE
// =============================================================================

interface CampaignHistoryState {
  events: HistoryEvent[];
  campaignId: string | null;
  realtimeCleanup: (() => void) | null;

  loadHistory: (campaignId: string) => Promise<void>;
  addEvent: (campaignId: string, eventType: HistoryEventType, data: HistoryEventData, sceneId?: string) => Promise<void>;
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getEncounterHistory: () => HistoryEvent[];
  getKillLog: () => HistoryEvent[];
  getXpTotal: (characterId: string) => number;
}

export const useCampaignHistoryStore = create<CampaignHistoryState>((set, get) => ({
  events: [],
  campaignId: null,
  realtimeCleanup: null,

  loadHistory: async (campaignId) => {
    set({ campaignId });
    try {
      const { data, error } = await supabase
        .from('machina_campaign_history')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        console.warn('[campaignHistoryStore] Failed to load history:', error);
        return;
      }

      const events = (data as unknown as DbHistoryEvent[]).map(dbToHistoryEvent);
      set({ events });
      console.log(`[campaignHistoryStore] Loaded ${events.length} history events`);
    } catch (err) {
      console.warn('[campaignHistoryStore] Failed to load history:', err);
    }
  },

  addEvent: async (campaignId, eventType, data, sceneId) => {
    try {
      const { data: inserted, error } = await supabase
        .from('machina_campaign_history')
        .insert({
          campaign_id: campaignId,
          scene_id: sceneId ?? null,
          event_type: eventType,
          data,
        })
        .select()
        .single();

      if (error) {
        console.warn('[campaignHistoryStore] Failed to insert event:', error);
        return;
      }

      const event = dbToHistoryEvent(inserted as unknown as DbHistoryEvent);
      set((state) => ({
        events: [event, ...state.events],
      }));
    } catch (err) {
      console.warn('[campaignHistoryStore] Failed to insert event:', err);
    }
  },

  subscribeToRealtime: (campaignId) => {
    get().unsubscribeFromRealtime();
    set({ campaignId });

    const { cleanup } = createSubscription({
      channelName: `campaign-history:${campaignId}`,
      table: 'machina_campaign_history',
      filter: `campaign_id=eq.${campaignId}`,
      event: 'INSERT',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT') {
          const db = payload.new as unknown as DbHistoryEvent;
          const event = dbToHistoryEvent(db);
          set((state) => {
            // Avoid duplicates from optimistic insert
            if (state.events.some(e => e.id === event.id)) return state;
            return { events: [event, ...state.events] };
          });
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  getEncounterHistory: () => {
    return get().events.filter(
      e => e.eventType === 'encounter_start' || e.eventType === 'encounter_end'
    );
  },

  getKillLog: () => {
    return get().events.filter(e => e.eventType === 'mob_killed');
  },

  getXpTotal: (characterId) => {
    return get().events
      .filter(e => e.eventType === 'xp_awarded' && (e.data as { characterId?: string }).characterId === characterId)
      .reduce((sum, e) => sum + ((e.data as { amount?: number }).amount ?? 0), 0);
  },
}));
